/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react'
import { Table, Form } from 'react-bootstrap-v5'
import Swal from 'sweetalert2'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { PlanningTabProps, downloadCsv, matchesSearch, monthLabel, postPlanning, quantity, toInputMonth, toNumber, usePlanningReport } from '../helpers/PlanningHelper'
import { PlanningNotice, PlanningTiles, ProductName } from './PlanningParts'

const PlanningDemandForecast: React.FC<PlanningTabProps> = ({ active, filters }) => {
	const [month, setMonth] = useState(toInputMonth(new Date()))
	const [search, setSearch] = useState('')
	const [edits, setEdits] = useState<{ [idProducts: number]: string }>({})
	const [saving, setSaving] = useState(false)

	const { loading, data, errors, reload } = usePlanningReport(
		active,
		'demand-forecast',
		filters.idProjects && month ? { idProjects: filters.idProjects, month, method: filters.method } : null
	)

	useEffect(() => {
		setEdits({})
	}, [data])

	/** The quantity the planner keeps for a row: what was typed, else the saved or calculated figure. */
	const finalQuantity = (row: any) => {
		const typed = edits[row.idProducts]
		return typed === undefined || typed.trim() === '' ? row.quantity : toNumber(typed)
	}

	const rows = useMemo(() => {
		return (data?.rows ?? []).filter((row: any) => matchesSearch(search, row.productName, row.productCode, row.itemName, row.categoryName))
	}, [data, search])

	const totals = useMemo(() => {
		const all = data?.rows ?? []
		return {
			system: data?.totals.systemVolume ?? 0,
			final: (data?.totals.volume ?? 0) + all.reduce((sum: number, row: any) => sum + (finalQuantity(row) - row.quantity) * row.size, 0),
			actual: data?.totals.actualVolume ?? 0,
			adjusted: all.filter((row: any) => Math.abs(finalQuantity(row) - row.systemQuantity) > 0.005).length,
		}
	}, [data, edits])

	const hasUnsavedEdits = (data?.rows ?? []).some((row: any) => Math.abs(finalQuantity(row) - row.quantity) > 0.005)

	const saveForecast = async () => {
		setSaving(true)
		const result = await postPlanning('demand-forecast/save', {
			idProjects: filters.idProjects,
			month: data.month,
			method: data.method,
			// lines left at the calculated figure are filled in by the server
			rows: data.rows
				.filter((row: any) => Math.abs(finalQuantity(row) - row.systemQuantity) > 0.005)
				.map((row: any) => ({ idProducts: row.idProducts, quantity: finalQuantity(row) })),
		})
		setSaving(false)

		if (result.success) {
			toast.success(result.successMessage || 'Forecast saved.', { position: 'bottom-right', autoClose: 3000 })
			reload()
		} else {
			result.errors.forEach((error) => toast.error(error, { position: 'bottom-right', autoClose: 5000 }))
		}
	}

	const removeForecast = async () => {
		const confirmed = await Swal.fire({
			title: 'Remove the saved forecast?',
			text: `${monthLabel(data.month)} will use the calculated forecast again.`,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Remove',
		})
		if (!confirmed.isConfirmed) {
			return
		}

		setSaving(true)
		const result = await postPlanning('demand-forecast/reset', { idProjects: filters.idProjects, month: data.month })
		setSaving(false)

		if (result.success) {
			toast.success(result.successMessage || 'Saved forecast removed.', { position: 'bottom-right', autoClose: 3000 })
			reload()
		} else {
			result.errors.forEach((error) => toast.error(error, { position: 'bottom-right', autoClose: 5000 }))
		}
	}

	const exportCsv = () => {
		downloadCsv(
			`demand-forecast-${data.month}.csv`,
			['Product', 'Product Code', 'Blend', 'Pack Size', 'Unit']
				.concat(data.historyMonths.map((ym: string) => monthLabel(ym)))
				.concat(['Months Sold (of 12)', 'Calculated Forecast', 'Forecast', 'Forecast Volume', `Delivered in ${monthLabel(data.month)}`]),
			rows.map((row: any) => [row.productName, row.productCode, row.itemName, row.packSize, row.unitName]
				.concat(data.historyMonths.map((ym: string) => row.history[ym]))
				.concat([row.monthsSold, row.systemQuantity, finalQuantity(row), finalQuantity(row) * row.size, row.actual]))
		)
	}

	const baseMonths: string[] = data?.baseMonths ?? []

	return (
		<>
			<p className='text-muted small mb-3'>
				Packs expected to be delivered in the month: the average of what was delivered (free items included) in the complete months before it.
				Change a figure where you know better and save; the Production Plan then uses the saved figures for that month.
			</p>

			<div className='row align-items-end'>
				<div className='col-md-2'>
					<Form.Group className='mb-3' controlId='forecastMonth'>
						<Form.Label>Month</Form.Label>
						<Form.Control type='month' value={month} onChange={(event) => setMonth(event.target.value)} />
					</Form.Group>
				</div>
				<div className='col-md-4'>
					<Form.Group className='mb-3' controlId='forecastSearch'>
						<Form.Label>Search</Form.Label>
						<Form.Control type='text' placeholder='Product, code, blend or category' value={search} onChange={(event) => setSearch(event.target.value)} />
					</Form.Group>
				</div>
				<div className='col-md-6'>
					<div className='mb-3'>
						{data?.editable && (
							<button className='btn btn-sm btn-primary p-3 me-2' onClick={saveForecast} disabled={saving || loading}>
								{saving && <span className='spinner-border spinner-border-sm me-1'></span>}
								<i className='fas fa-save me-1'></i>Save forecast
							</button>
						)}
						{data?.editable && data?.saved && (
							<button className='btn btn-sm btn-outline-danger p-3 me-2' onClick={removeForecast} disabled={saving || loading}>
								Remove saved forecast
							</button>
						)}
						<button className='btn btn-sm btn-outline-primary p-3' onClick={exportCsv} disabled={rows.length === 0}>
							<i className='fas fa-download me-1'></i>Excel (CSV)
						</button>
					</div>
				</div>
			</div>

			<PlanningNotice loading={loading} errors={errors} hasData={!!data} />

			{data && errors.length === 0 && (
				<>
					<div className={`alert py-3 ${data.saved ? 'alert-success' : 'alert-secondary'}`}>
						{monthLabel(data.month)}: average of {monthLabel(baseMonths[0])} to {monthLabel(baseMonths[baseMonths.length - 1])}.{' '}
						{data.saved
							? `Saved forecast in use (${data.saved.products} products, saved by ${data.saved.by} on ${data.saved.at}).`
							: 'Not saved: the calculated forecast is in use.'}
						{!data.editable && ' This month has passed, so it can no longer be changed.'}
						{hasUnsavedEdits && <strong> You have changes that are not saved.</strong>}
					</div>

					<PlanningTiles
						tiles={[
							{ label: 'Products', value: String(data.rows.length) },
							{ label: 'Calculated volume', value: quantity(totals.system) },
							{ label: 'Forecast volume', value: quantity(totals.final) },
							{ label: 'Products adjusted', value: String(totals.adjusted) },
							{ label: `Delivered in ${monthLabel(data.month)}`, value: quantity(totals.actual) },
						]}
					/>

					<Table responsive striped bordered hover size='sm' className='mb-0 bg-white'>
						<thead className='table-light'>
							<tr>
								<th>SL</th>
								<th>Product</th>
								<th>Pack Size</th>
								<th>Unit</th>
								{data.historyMonths.map((ym: string) => (
									<th key={ym} className={`text-end ${baseMonths.includes(ym) ? '' : 'text-muted'}`} style={{ whiteSpace: 'nowrap' }} title={baseMonths.includes(ym) ? 'Counted in the average' : 'Shown for reference'}>
										{monthLabel(ym)}
									</th>
								))}
								<th className='text-end' title='Months with a delivery, out of the last 12'>Months Sold</th>
								<th className='text-end'>Calculated</th>
								<th className='text-end' style={{ minWidth: 110 }}>Forecast</th>
								<th className='text-end'>Volume</th>
								<th className='text-end' title={`Delivered in ${monthLabel(data.month)} so far`}>Delivered</th>
							</tr>
						</thead>
						<tbody>
							{rows.length === 0 && (
								<tr>
									<td colSpan={data.historyMonths.length + 9} className='text-center text-muted py-4'>No product was delivered in the months this forecast looks at.</td>
								</tr>
							)}
							{rows.map((row: any, index: number) => {
								const final = finalQuantity(row)
								const adjusted = Math.abs(final - row.systemQuantity) > 0.005

								return (
									<tr key={row.idProducts}>
										<td>{index + 1}</td>
										<td><ProductName row={row} /></td>
										<td>{row.packSize}</td>
										<td>{row.unitName}</td>
										{data.historyMonths.map((ym: string) => (
											<td key={ym} className={`text-end ${baseMonths.includes(ym) ? '' : 'text-muted'}`}>{row.history[ym] ? quantity(row.history[ym], 2) : ''}</td>
										))}
										<td className='text-end'>{row.monthsSold}</td>
										<td className='text-end'>{quantity(row.systemQuantity, 2)}</td>
										<td className='text-end'>
											{data.editable ? (
												<input
													type='number'
													min='0'
													step='any'
													className={`form-control form-control-sm text-end ${adjusted ? 'bg-light-warning' : ''}`}
													aria-label={`Forecast for ${row.productName} ${row.packSize}`}
													value={edits[row.idProducts] ?? String(row.quantity)}
													onChange={(event) => setEdits({ ...edits, [row.idProducts]: event.target.value })}
												/>
											) : (
												<span className={adjusted ? 'fw-bold' : ''}>{quantity(final, 2)}</span>
											)}
										</td>
										<td className='text-end'>{quantity(final * row.size)} {row.itemUnitName}</td>
										<td className='text-end'>{row.actual ? quantity(row.actual, 2) : ''}</td>
									</tr>
								)
							})}
						</tbody>
					</Table>
				</>
			)}
		</>
	)
}

export default PlanningDemandForecast
